import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import StatusBadge from "@/components/StatusBadge";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PROVINCIES } from "@/lib/jobOptions";
import { LayoutDashboard, Users, Briefcase, Trash2, Wrench, Building2, FileText, FolderOpen, User, Euro, Crown, Receipt, TrendingUp, Megaphone } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import SponsorsTab from "@/components/admin/SponsorsTab";
import EmailDiagnostics from "@/components/admin/EmailDiagnostics";
import { toast } from "sonner";

const TABS = [
  { key: "overzicht", label: "Overzicht", icon: LayoutDashboard },
  { key: "omzet", label: "Omzet", icon: TrendingUp },
  { key: "gebruikers", label: "Gebruikers", icon: Users },
  { key: "opdrachten", label: "Opdrachten", icon: Briefcase },
  { key: "offertes", label: "Offertes", icon: FileText },
  { key: "sponsors", label: "Sponsors", icon: Megaphone },
];

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="label-caps">{label}</span>
        <Icon className="w-5 h-5 text-orange-500" />
      </div>
      <p className="font-display text-4xl font-black text-slate-950">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("overzicht");
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [offertes, setOffertes] = useState([]);
  const [provFilter, setProvFilter] = useState("alle");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [testOnly, setTestOnly] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, u, j, r, o] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/jobs"), api.get("/admin/revenue"), api.get("/admin/offertes"),
      ]);
      setStats(s.data); setUsers(u.data.users); setJobs(j.data.jobs); setRevenue(r.data); setOffertes(o.data.offertes);
    } catch (e) { toast.error("Laden mislukt"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteUser = async (id) => {
    try { await api.delete(`/admin/users/${id}`); toast.success("Gebruiker verwijderd"); setSelectedIds((s) => s.filter((x) => x !== id)); load(); }
    catch (e) { toast.error("Verwijderen mislukt"); }
  };

  const deleteJob = async (id) => {
    try { await api.delete(`/jobs/${id}`); toast.success("Opdracht verwijderd — de opdrachtgever is verwittigd."); load(); }
    catch (e) { toast.error("Verwijderen mislukt"); }
  };

  const deleteOfferte = async (id) => {
    try { await api.delete(`/admin/offertes/${id}`); toast.success("Offerteaanvraag verwijderd"); load(); }
    catch (e) { toast.error("Verwijderen mislukt"); }
  };

  const toggleSelect = (id) =>
    setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/admin/users/bulk-delete", { ids: selectedIds });
      toast.success(`${data.verwijderd} gebruiker(s) verwijderd`);
      setSelectedIds([]);
      load();
    } catch (e) { toast.error("Verwijderen mislukt"); }
    finally { setBulkDeleting(false); }
  };

  const sub = { overzicht: "Platform statistieken", omzet: "Commerciële groei", gebruikers: "Professionals & bedrijven", opdrachten: "Alle opdrachten", offertes: "Alle offerteaanvragen", sponsors: "Banners & advertenties" }[tab];
  const title = { overzicht: "Beheer overzicht", omzet: "Omzet", gebruikers: "Gebruikers", opdrachten: "Opdrachten", offertes: "Offertes", sponsors: "Sponsors" }[tab];
  const eur = (n) => `€${Number(n || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const isTestAccount = (u) => /(@example\.com|@test\.|@mailinator\.com)/i.test(u.email || "") || /test/i.test(u.email || "");
  const filteredUsers = users
    .filter((u) => provFilter === "alle" || u.provincie === provFilter)
    .filter((u) => !testOnly || isTestAccount(u));
  const filteredJobs = provFilter === "alle" ? jobs : jobs.filter((j) => j.provincie === provFilter);
  const ProvincieFilter = () => (
    <div className="mb-4 w-64">
      <Select value={provFilter} onValueChange={setProvFilter}>
        <SelectTrigger data-testid="admin-filter-provincie" className="rounded-sm border-slate-300 bg-white"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="alle">Alle provincies</SelectItem>
          {PROVINCIES.map((g) => (
            <div key={g.groep}>
              <div className="px-2 py-1.5 text-xs font-bold text-slate-400 uppercase tracking-wide">{g.groep}</div>
              {g.opties.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </div>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <DashboardLayout title={title} subtitle={`Beheerder · ${sub}`} tabs={TABS} active={tab} onTab={setTab}>
      {tab === "overzicht" && <EmailDiagnostics />}
      {tab === "overzicht" && stats && (
        <div className="grid sm:grid-cols-2 xl:grid-cols-6 gap-4">
          <Stat icon={Wrench} label="Professionals" value={stats.monteurs} />
          <Stat icon={Building2} label="Bedrijven" value={stats.bedrijven} />
          <Stat icon={User} label="Particulieren" value={stats.particulieren} />
          <Stat icon={Briefcase} label="Opdrachten" value={stats.opdrachten} />
          <Stat icon={FolderOpen} label="Open" value={stats.open_opdrachten} />
          <Stat icon={FileText} label="Aanvragen" value={stats.aanvragen} />
        </div>
      )}

      {tab === "omzet" && revenue && (
        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <Stat icon={TrendingUp} label="Totale omzet" value={eur(revenue.omzet_totaal)} />
            <Stat icon={Crown} label="Actieve Pro-leden" value={revenue.actieve_pro_gebruikers} />
            <Stat icon={Receipt} label={`Pro-betalingen (${revenue.pro_aantal})`} value={eur(revenue.pro_omzet)} />
            <Stat icon={Euro} label={`Betaalde opdrachten (${revenue.job_aantal})`} value={eur(revenue.job_omzet)} />
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-center justify-between mb-4">
              <span className="label-caps">Omzet per maand</span>
              <span className="text-xs font-semibold text-slate-400">Laatste 6 maanden</span>
            </div>
            <div className="h-64" data-testid="revenue-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenue.omzet_per_maand} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="proFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f97316" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="jobFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0f172a" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#0f172a" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="maand" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} width={48}
                    tickFormatter={(v) => `€${v}`} />
                  <Tooltip formatter={(v, name) => [eur(v), name]} contentStyle={{ borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 13 }} />
                  <Legend wrapperStyle={{ fontSize: 12, fontWeight: 600 }} />
                  <Area type="monotone" dataKey="pro" name="Pro-abonnementen" stroke="#f97316" strokeWidth={2.5} fill="url(#proFill)" />
                  <Area type="monotone" dataKey="opdracht" name="Opdrachten" stroke="#0f172a" strokeWidth={2.5} fill="url(#jobFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
              <span className="label-caps">Recente betalingen</span>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-white">
                  <TableHead>Type</TableHead>
                  <TableHead>Bedrag</TableHead>
                  <TableHead>Datum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {revenue.recente_transacties.length === 0 && (
                  <TableRow><TableCell colSpan={3} className="text-center py-10 text-slate-400">Nog geen betalingen.</TableCell></TableRow>
                )}
                {revenue.recente_transacties.map((t, i) => (
                  <TableRow key={`${t.created_at}-${i}`} data-testid="admin-revenue-row">
                    <TableCell>
                      <span className="text-xs font-bold uppercase tracking-wide bg-slate-100 px-2 py-0.5 rounded-sm">
                        {t.kind === "pro" ? "Pro-abonnement" : "Opdracht"}
                      </span>
                    </TableCell>
                    <TableCell className="font-semibold">{eur(t.amount)}</TableCell>
                    <TableCell className="text-slate-500">{t.created_at ? new Date(t.created_at).toLocaleString("nl-NL") : "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {tab === "gebruikers" && (
        <div>
          <div className="flex flex-wrap items-center gap-4 mb-2">
            <ProvincieFilter />
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 cursor-pointer pb-4">
              <Checkbox data-testid="filter-testaccounts" checked={testOnly} onCheckedChange={(c) => setTestOnly(!!c)} />
              Alleen testaccounts
            </label>
          </div>
          <p className="text-xs text-slate-400 mb-4 -mt-2">Testaccounts = e-mail op <code>@example.com</code>, <code>@test.*</code>, <code>@mailinator.com</code> of die "test" bevat. Combineer met "selecteer alles" om ze in één keer te verwijderen.</p>
          {selectedIds.length > 0 && (
            <div data-testid="bulk-action-bar" className="mb-4 flex items-center justify-between bg-red-50 border border-red-200 rounded-md px-4 py-3">
              <span className="text-sm font-semibold text-red-800">{selectedIds.length} geselecteerd</span>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid="bulk-delete-btn" disabled={bulkDeleting}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-sm font-bold">
                    <Trash2 className="w-4 h-4 mr-2" /> Verwijder geselecteerde ({selectedIds.length})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="bulk-delete-dialog" className="rounded-md">
                  <AlertDialogHeader>
                    <AlertDialogTitle>{selectedIds.length} gebruiker(s) verwijderen?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Deze actie kan niet ongedaan worden gemaakt. De geselecteerde accounts worden permanent verwijderd. Admin-accounts worden nooit verwijderd.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="bulk-delete-cancel">Annuleren</AlertDialogCancel>
                    <AlertDialogAction data-testid="bulk-delete-confirm" onClick={bulkDelete}
                      className="bg-red-600 hover:bg-red-700 text-white">
                      Definitief verwijderen
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10">
                  <Checkbox data-testid="select-all-users"
                    checked={filteredUsers.length > 0 && filteredUsers.every((u) => selectedIds.includes(u.id))}
                    onCheckedChange={(c) => {
                      const ids = filteredUsers.map((u) => u.id);
                      setSelectedIds((s) => (c ? Array.from(new Set([...s, ...ids])) : s.filter((x) => !ids.includes(x))));
                    }} />
                </TableHead>
                <TableHead>Naam</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Provincie</TableHead>
                <TableHead className="text-right">Actie</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">Geen gebruikers.</TableCell></TableRow>
              )}
              {filteredUsers.map((u) => (
                <TableRow key={u.id} data-testid="admin-user-row" data-state={selectedIds.includes(u.id) ? "selected" : undefined}>
                  <TableCell>
                    <Checkbox data-testid="select-user-checkbox"
                      checked={selectedIds.includes(u.id)} onCheckedChange={() => toggleSelect(u.id)} />
                  </TableCell>
                  <TableCell className="font-semibold">{u.name}</TableCell>
                  <TableCell className="text-slate-500">{u.email}</TableCell>
                  <TableCell>
                    <span className="text-xs font-bold uppercase tracking-wide bg-slate-100 px-2 py-0.5 rounded-sm">{u.role}</span>
                  </TableCell>
                  <TableCell className="text-slate-500">{u.provincie || "-"}</TableCell>
                  <TableCell className="text-right">
                    <Button data-testid="delete-user-btn" onClick={() => deleteUser(u.id)} variant="ghost" size="sm"
                      className="text-red-600 hover:bg-red-50 rounded-sm">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </div>
      )}

      {tab === "opdrachten" && (
        <div>
          <ProvincieFilter />
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Titel</TableHead>
                <TableHead>Bedrijf</TableHead>
                <TableHead>Provincie</TableHead>
                <TableHead>Professional</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredJobs.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">Geen opdrachten.</TableCell></TableRow>
              )}
              {filteredJobs.map((j) => (
                <TableRow key={j.id} data-testid="admin-job-row">
                  <TableCell className="font-semibold">{j.titel}</TableCell>
                  <TableCell className="text-slate-500">{j.company_name}</TableCell>
                  <TableCell className="text-slate-500">{j.provincie || j.locatie || "-"}</TableCell>
                  <TableCell className="text-slate-500">{j.monteur_name || "-"}</TableCell>
                  <TableCell><StatusBadge status={j.status} /></TableCell>
                  <TableCell className="text-right">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button data-testid="delete-job-btn" variant="ghost" size="sm" className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Opdracht verwijderen?</AlertDialogTitle>
                          <AlertDialogDescription>
                            "{j.titel}" wordt definitief verwijderd. De opdrachtgever ({j.company_name}) krijgt hiervan een e-mail.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuleren</AlertDialogCancel>
                          <AlertDialogAction data-testid="confirm-delete-job-btn" onClick={() => deleteJob(j.id)} className="bg-red-600 hover:bg-red-700">Verwijderen</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </div>
      )}

      {tab === "offertes" && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Aanvrager</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Gemeente</TableHead>
                <TableHead>Elementen</TableHead>
                <TableHead>Aangeschreven bedrijven</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Datum</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {offertes.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">Geen offerteaanvragen.</TableCell></TableRow>
              )}
              {offertes.map((o) => (
                <TableRow key={o.id} data-testid="admin-offerte-row">
                  <TableCell className="font-semibold">{o.naam}</TableCell>
                  <TableCell className="text-slate-500 text-xs">
                    <div>{o.email}</div>
                    <div>{o.telefoon}</div>
                  </TableCell>
                  <TableCell className="text-slate-500">{o.adres_gemeente || "-"}</TableCell>
                  <TableCell className="text-slate-500">{o.aantal_elementen}</TableCell>
                  <TableCell className="text-slate-500 text-xs max-w-[220px]">{(o.bedrijf_namen_lijst || []).join(", ") || "-"}</TableCell>
                  <TableCell><StatusBadge status={o.status} /></TableCell>
                  <TableCell className="text-slate-500 text-xs">{o.created_at ? new Date(o.created_at).toLocaleDateString("nl-NL") : "-"}</TableCell>
                  <TableCell className="text-right">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button data-testid="delete-offerte-btn" variant="ghost" size="sm" className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Offerteaanvraag verwijderen?</AlertDialogTitle>
                          <AlertDialogDescription>
                            De aanvraag van {o.naam} wordt definitief verwijderd.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annuleren</AlertDialogCancel>
                          <AlertDialogAction data-testid="confirm-delete-offerte-btn" onClick={() => deleteOfferte(o.id)} className="bg-red-600 hover:bg-red-700">Verwijderen</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {tab === "sponsors" && <SponsorsTab />}
    </DashboardLayout>
  );
}
